import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import AuthLayout from "@/components/AuthLayout";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) { setError("Passwords do not match"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (!token) { setError("Invalid reset link. Please request a new one."); return; }
    setLoading(true);
    try {
      await API.post("/auth/reset-password", { token, password });
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const labelCls = "text-xs uppercase tracking-[0.2em] text-slate-500 font-bold";

  return (
    <AuthLayout
      eyebrow="Access / New Password"
      title="Set a new password"
      subtitle="Choose a strong password you haven't used before."
    >
      <div data-testid="reset-password-page" />
      {success ? (
        <div>
          <div
            className="bg-emerald-50 border border-emerald-200 rounded-md px-4 py-3 text-sm text-emerald-700 mb-6"
            data-testid="reset-success-msg"
          >
            Password reset successfully. You can now sign in with your new password.
          </div>
          <Link
            to="/login"
            className="inline-block bg-[#002FA7] hover:bg-[#00227A] text-white font-medium px-6 py-2.5 rounded-md text-sm tracking-tight"
            data-testid="go-to-login-btn"
          >
            Sign In
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div
              className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              data-testid="reset-error"
            >
              {error}
            </div>
          )}
          <div className="space-y-1.5">
            <Label className={labelCls}>New Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-11"
              data-testid="reset-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Confirm Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="h-11"
              data-testid="reset-confirm-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-[#002FA7] hover:bg-[#00227A] text-white font-medium tracking-tight"
            data-testid="reset-submit-btn"
          >
            {loading ? "Resetting…" : "Reset Password"}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
